import { FunctionComponent, ReactNode, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import type { RootState } from "../../../app/rootReducer";
import FacilityReportSVG from "../../../components/Reports/FacilityReport/facility-report-svg.component";
import SelectionChips from "../../../components/Reports/FacilityReport/selection-chips.component";
import type { CapacityObject, FloorPlanMemo } from "../../Reports/typings/reports.types";
import {
  MANAGEMENT_FLOOR_PLAN_SVG_HEIGHT_DESKTOP,
  MANAGEMENT_FLOOR_PLAN_SVG_HEIGHT_MOBILE,
  MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX
} from "../utils/management-toolbar.constants";
import { managementTouchIconButtonSx } from "../utils/management-touch-target.styles";

type ManagementFloorPlanPanelProps = {
  expanded: boolean;
  /** Initial load: show spinner instead of collapsed hint / content */
  loading: boolean;
  /** Refetch while expanded: keep content, show overlay spinner */
  loadingOverlay?: boolean;
  /** Primary location · floor label. */
  floorName?: string;
  /** Compare location · floor label. */
  compareFloorName?: string;
  /** Override collapsed-state hint (e.g. All locations / mobile working). */
  collapsedHint?: string;
  /** Capacity-style floor plan payload keyed by floor inventory id. */
  floorData?: FloorPlanMemo;
  /** Capacity entity used for workplace coloring on the SVG. */
  entityData?: (CapacityObject | undefined)[];
  /**
   * Capacity-style empty half: shown next to a single primary plan when compare is not active.
   * Hidden when two floors are already in floorData / entityData.
   */
  compareSlot?: ReactNode;
  /** When a compare floor is active, show clear control on the compare chip. */
  compareActive?: boolean;
  onClearCompare?: () => void;
  /** Clears place/zone selection chips (floor charts restore via React Query cache). */
  onClearPlaceZoneSelection?: () => void;
};

const floorPlanChipSx = {
  color: "text.primary",
  "& .MuiChip-icon": { marginLeft: 1, marginRight: -0.5 }
} as const;

/** Tiny series swatch so chips stay neutral while matching chart colors. */
function FloorPlanSeriesDot({ color }: { color: "primary.main" | "warning.main" }) {
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: color,
        ml: 1,
        flexShrink: 0
      }}
      aria-hidden
    />
  );
}

/** Compact vertical rule for the floor-plan header chip row. */
function FloorPlanHeaderDivider() {
  return (
    <Divider
      orientation="vertical"
      aria-hidden
      data-testid="management-floor-plan-header-divider"
      sx={{ height: 16, alignSelf: "center", borderColor: "divider" }}
    />
  );
}

/**
 * Collapsible floor-plan panel for Management location detail.
 * Renders FacilityReportSVG (1–2 floors) and an optional compare picker slot.
 */
export const ManagementFloorPlanPanel: FunctionComponent<ManagementFloorPlanPanelProps> = ({
  expanded,
  loading,
  loadingOverlay = false,
  floorName,
  compareFloorName,
  collapsedHint,
  floorData,
  entityData,
  compareSlot,
  compareActive = false,
  onClearCompare,
  onClearPlaceZoneSelection
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const svgHeight = isMobileViewport
    ? MANAGEMENT_FLOOR_PLAN_SVG_HEIGHT_MOBILE
    : MANAGEMENT_FLOOR_PLAN_SVG_HEIGHT_DESKTOP;
  const selectionOpen = useSelector(
    (state: RootState) => state.report.selectedZoneOrWorkplace?.open === true
  );
  const collapsedMessage = collapsedHint ?? t("_management_floorPlanCollapsedHint");
  const [isFloorFullMode, setIsFloorFullMode] = useState<{ index: number | null; open: boolean }>({
    index: null,
    open: false
  });
  const canShowSvg =
    Boolean(floorData) && Boolean(entityData?.[0]?.capacity?.capacityPerformancePerWorkplace);
  const floorCount = entityData?.filter(
    entity => entity?.capacity?.capacityPerformancePerWorkplace
  ).length;
  const showCompareSlot = Boolean(compareSlot) && (floorCount ?? 0) < 2;
  const hasFloorLabels = Boolean(floorName) || Boolean(compareFloorName);
  const showSelection = Boolean(onClearPlaceZoneSelection) && selectionOpen;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: "8px" }}
      data-testid="management-floor-plan-panel"
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="subtitle2"
          component="h2"
          sx={{ fontWeight: 600 }}
          data-testid="management-floor-plan-title"
        >
          {t("_management_floorPlan")}
        </Typography>
        <Tooltip title={<Typography>{t("_management_floorPlanMetricsHelp")}</Typography>}>
          <IconButton
            size="small"
            aria-label={t("_management_floorPlanMetricsHelp")}
            data-testid="management-floor-plan-metrics-help"
            sx={managementTouchIconButtonSx}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        {hasFloorLabels ? (
          <>
            <FloorPlanHeaderDivider />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              {floorName ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={floorName}
                  data-testid="management-floor-plan-primary-chip"
                  icon={<FloorPlanSeriesDot color="primary.main" />}
                  sx={floorPlanChipSx}
                />
              ) : null}
              {compareFloorName ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={compareFloorName}
                  onDelete={compareActive ? onClearCompare : undefined}
                  data-testid="management-floor-plan-compare-chip"
                  aria-label={
                    compareActive
                      ? `${compareFloorName}. ${t("_management_clearCompare")}`
                      : undefined
                  }
                  icon={<FloorPlanSeriesDot color="warning.main" />}
                  sx={{
                    ...floorPlanChipSx,
                    "& .MuiChip-deleteIcon": { color: "text.secondary" }
                  }}
                />
              ) : null}
            </Stack>
          </>
        ) : null}
        {showSelection && onClearPlaceZoneSelection ? (
          <>
            <FloorPlanHeaderDivider />
            <SelectionChips dense refetchOnRemove={onClearPlaceZoneSelection} />
          </>
        ) : null}
        {showSelection && onClearPlaceZoneSelection ? (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={onClearPlaceZoneSelection}
            data-testid="management-floor-plan-clear-selection"
            data-min-height={MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX}
            sx={{
              minHeight: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
              py: 0.5,
              px: 1.5,
              ml: "auto",
              textTransform: "none",
              fontWeight: 500
            }}
          >
            {t("_management_clearSelection")}
          </Button>
        ) : null}
      </Stack>

      {loading && !expanded && (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
          data-testid="management-floor-plan-loading"
        >
          <CircularProgress size={32} aria-label={t("_management_loadingFloorPlan")} />
        </Box>
      )}

      {/*
        Mount Pixi only when expanded with a stable box size. Do not wrap in MUI Collapse:
        DisplayViewport measures parent once on mount; Collapse starts near height 0, so the
        stage locks a wrong size and the plan looks clipped to a corner.
      */}
      {expanded ? (
        <Box
          sx={{
            position: "relative",
            minHeight: isMobileViewport ? 180 : 220,
            bgcolor: "action.hover",
            borderRadius: 1,
            overflow: "hidden"
          }}
          data-testid="management-floor-plan-content"
        >
          {canShowSvg && floorData && entityData ? (
            <Grid2
              container
              data-testid="management-floor-plan-svg"
              data-svg-height={svgHeight}
              sx={{
                /* Capacity compare/fullscreen control — not needed on Management detail */
                '& [data-testid="report-fullscreen-btn"]': { display: "none" }
              }}
            >
              <FacilityReportSVG
                floorData={floorData}
                zoneBookingReport={false}
                isFloorFullMode={isFloorFullMode}
                setIsFloorFullMode={setIsFloorFullMode}
                entityData={entityData}
                svgHeight={svgHeight}
              />
              {showCompareSlot && (
                <Grid2 size={{ xs: 12, md: 6 }} data-testid="management-floor-compare-column">
                  {compareSlot}
                </Grid2>
              )}
            </Grid2>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t("_management_floorPlanUnavailable")}
              </Typography>
            </Box>
          )}

          {loadingOverlay && (
            <Box
              data-testid="management-floor-plan-overlay-loading"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.background.paper, 0.7)
              }}
            >
              <CircularProgress size={32} aria-label={t("_management_loadingFloorPlan")} />
            </Box>
          )}
        </Box>
      ) : null}

      {!expanded && !loading && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="management-floor-plan-collapsed"
        >
          {collapsedMessage}
        </Typography>
      )}
    </Paper>
  );
};
